/**
 * Contrato de tipos de notificacao.
 *
 * Single source of truth para front e back.
 * Ao adicionar um novo tipo:
 * 1. Adicione aqui no NOTIFICATION_TYPES
 * 2. No backend, atualize app/schemas/notification_types.py
 */

import {
  Bell,
  Warning,
  WarningCircle,
  Clock,
  UserPlus,
  CheckCircle,
  XCircle,
  Prohibit,
  FileDoc,
  PencilSimpleLine,
  FileXls,
} from 'phosphor-react';

export const NOTIFICATION_TYPES = {
  card_deadline_warning: {
    label: 'Vence em 2 dias',
    toastLabel: 'Aviso de vencimento',
    color: 'yellow.500',
    icon: Clock,
  },
  card_deadline_urgent: {
    label: 'Vence amanha',
    toastLabel: 'Vencimento urgente',
    color: 'orange.500',
    icon: Warning,
  },
  card_deadline_today: {
    label: 'Vence hoje',
    toastLabel: 'Vence hoje',
    color: 'red.500',
    icon: WarningCircle,
  },
  card_deadline_overdue: {
    label: 'Vencido',
    toastLabel: 'Card vencido',
    color: 'red.700',
    icon: WarningCircle,
  },
  card_assigned: {
    label: 'Atribuido',
    toastLabel: 'Card atribuido',
    color: 'blue.500',
    icon: Bell,
  },
  card_moved: {
    label: 'Movido',
    toastLabel: 'Card movido',
    color: 'purple.500',
    icon: Bell,
  },
  card_comment: {
    label: 'Comentario',
    toastLabel: 'Novo comentario',
    color: 'teal.500',
    icon: Bell,
  },
  system: {
    label: 'Sistema',
    toastLabel: 'Sistema',
    color: 'gray.500',
    icon: Bell,
  },
  solicitacao_acesso_nova: {
    label: 'Nova solicitacao',
    toastLabel: 'Solicitacao de acesso',
    color: 'blue.500',
    icon: UserPlus,
  },
  solicitacao_acesso_aprovada: {
    label: 'Aprovada',
    toastLabel: 'Acesso aprovado',
    color: 'green.500',
    icon: CheckCircle,
  },
  solicitacao_acesso_rejeitada: {
    label: 'Rejeitada',
    toastLabel: 'Acesso rejeitado',
    color: 'red.500',
    icon: XCircle,
  },
  solicitacao_acesso_cancelada: {
    label: 'Cancelada',
    toastLabel: 'Solicitacao cancelada',
    color: 'gray.500',
    icon: Prohibit,
  },
  contrato_gerado: {
    label: 'Contrato gerado',
    toastLabel: 'Contrato pronto!',
    color: 'green.500',
    icon: FileDoc,
  },
  contrato_campos_pendentes: {
    label: 'Campos pendentes',
    toastLabel: 'Contrato gerado — preencha os campos',
    color: 'orange.500',
    icon: PencilSimpleLine,
  },
  contrato_erro: {
    label: 'Erro no contrato',
    toastLabel: 'Erro ao gerar contrato',
    color: 'red.500',
    icon: XCircle,
  },
  // Preenchimento do template da outra parte (08/09). Dois tipos porque o gate
  // decide se o documento pode ir pra assinatura — o operador precisa saber
  // ANTES de abrir se há bloqueio, não depois de já ter enviado.
  contrato_preenchido: {
    label: 'Contrato preenchido',
    toastLabel: 'Contrato preenchido — confira antes de enviar',
    color: 'green.500',
    icon: FileDoc,
  },
  contrato_preenchido_com_bloqueio: {
    label: 'Contrato precisa de revisão',
    toastLabel: 'Contrato preenchido com pontos a revisar',
    color: 'orange.500',
    icon: PencilSimpleLine,
  },
  // Tipo próprio, e não `contrato_erro`: aquele já pertence ao `gerar-contrato`
  // legado, e o registry admite só UM dono por tipo — a tela nova desligaria o
  // spinner numa falha do fluxo velho.
  contrato_preenchimento_erro: {
    label: 'Erro ao preencher contrato',
    toastLabel: 'Erro ao preencher o contrato',
    color: 'red.500',
    icon: XCircle,
  },
  // Fase 6 — geracao IA-first async (engine v2). Roteado em
  // pages/societario/notificacoes.ts para o emitter `pages/societario/eventos`.
  // ⚠️ A CHAVE mantém o sufixo `_v2` por compatibilidade — é o canal que o
  // provider e o emitter já roteiam, e desde 2026-08-19 a geração assíncrona do
  // v3 usa o mesmo caminho (o engine vai no payload). Mas o RÓTULO é o que o
  // operador lê: "(v2)" aparecia no painel para um contrato gerado pelo motor
  // novo, e não significa nada para quem opera.
  societario_contrato_gerado_v2: {
    label: 'Contrato societário',
    toastLabel: 'Contrato gerado',
    color: 'purple.500',
    icon: FileDoc,
  },
  // Sessao 2026-05-08 (Fase D): eventos cross-user do dept societario.
  societario_contrato_gerado_v2_dept: {
    label: 'Contrato societário do departamento',
    toastLabel: 'Geração concluída pelo departamento',
    color: 'purple.500',
    icon: FileDoc,
  },
  societario_rascunho_atualizado: {
    label: 'Rascunho societário atualizado',
    toastLabel: 'Rascunho atualizado pelo departamento',
    color: 'blue.500',
    icon: PencilSimpleLine,
  },
  societario_contrato_arquivado: {
    label: 'Contrato societário arquivado',
    toastLabel: 'Contrato arquivado',
    color: 'gray.500',
    icon: FileDoc,
  },
  estatuto_preenchido: {
    label: 'Estatuto preenchido',
    toastLabel: 'Estatuto aguardando validação',
    color: 'blue.500',
    icon: PencilSimpleLine,
  },
  estatuto_assinado: {
    label: 'Estatuto assinado',
    toastLabel: 'Assinatura concluída',
    color: 'green.500',
    icon: CheckCircle,
  },
  // Push do webhook Autentique — cobre os status de assinatura (assinado /
  // rejeitado / pendente); o detalhe vem no campo `status` do evento.
  estatuto_assinatura: {
    label: 'Atualização de assinatura',
    toastLabel: 'Assinatura atualizada',
    color: 'green.500',
    icon: CheckCircle,
  },
  // Assinatura de contrato de evento (webhook Autentique). Status no
  // campo `status` do evento (assinado / rejeitado).
  contrato_assinatura: {
    label: 'Assinatura do contrato',
    toastLabel: 'Assinatura atualizada',
    color: 'green.500',
    icon: CheckCircle,
  },
  // Excel da Conferência Fiscal gerado em segundo plano. Clique leva para a
  // tela e baixa o arquivo (`pages/modulesHome/Integration/conferenciaFiscal/notificacoes.ts`).
  conferencia_exportacao_pronta: {
    label: 'Planilha pronta',
    toastLabel: 'Planilha da conferência pronta',
    color: 'green.500',
    icon: FileXls,
  },
  conferencia_exportacao_erro: {
    label: 'Erro na planilha',
    toastLabel: 'Não foi possível gerar a planilha',
    color: 'red.500',
    icon: XCircle,
  },
} as const;

export type TipoNotificacao = keyof typeof NOTIFICATION_TYPES;

// Config para um tipo especifico (com fallback)
export function getNotificationConfig(tipo: string) {
  return (
    NOTIFICATION_TYPES[tipo as TipoNotificacao] || {
      label: 'Notificacao',
      toastLabel: 'Nova notificacao',
      color: 'gray.500',
      icon: Bell,
    }
  );
}
